import { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiFetch, v1Path } from '../../lib/api';
import { fetchInitiativeWorkspace } from '../../lib/initiatives';
import { cn } from '../../lib/utils';
import type { ListResponse, WorkItemSlim } from '../../lib/work-types';

export type InitiativeOption = { id: string; key: string; title: string };

const NONE = '__none__';

/** Every initiative in the org, for linking a task to one. */
export function useInitiativeOptions(enabled = true): { options: InitiativeOption[]; loading: boolean } {
  const [options, setOptions] = useState<InitiativeOption[]>([]);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    fetchInitiativeWorkspace()
      .then((workspace) =>
        apiFetch<ListResponse<WorkItemSlim>>(
          `${v1Path(`/projects/${workspace.project.id}/work-items`)}?types=initiative&show_sub_items=false&order_by=sort_order&limit=100&offset=0`,
        ),
      )
      .then((page) => {
        if (!cancelled) setOptions(page.items.map((row) => ({ id: row.id, key: row.key, title: row.title })));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { options, loading };
}

export default function InitiativePicker({
  value,
  current,
  onChange,
  disabled,
  className,
}: {
  value: string | null;
  /** Shown while options load, or when the linked initiative is not in the list. */
  current?: { id: string; key: string; title: string } | null;
  onChange: (initiativeId: string | null) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { options, loading } = useInitiativeOptions(!disabled);
  const list =
    current && !options.some((option) => option.id === current.id) ? [current, ...options] : options;
  const selected = list.find((option) => option.id === value);

  return (
    <Select
      value={value ?? NONE}
      disabled={disabled}
      onValueChange={(next) => onChange(next === NONE ? null : next)}
    >
      <SelectTrigger aria-label="Initiative" className={cn('h-8 w-auto max-w-xs gap-1.5', className)}>
        <Target className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        {selected ? <span className="truncate">{selected.title}</span> : <SelectValue placeholder="No initiative" />}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>No initiative</SelectItem>
        {list.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            <span className="font-mono text-[11px] text-muted-foreground">{option.key}</span> {option.title}
          </SelectItem>
        ))}
        {loading ? <p className="px-2 py-1.5 text-xs text-muted-foreground">Loading initiatives…</p> : null}
      </SelectContent>
    </Select>
  );
}
